import { Inject, Injectable, OnDestroy } from '@angular/core';
import { DwOrmDataService, DwOrmDataServiceToken, DwCacheService, DwCacheStorageLocation, DwConfigService,
  DwConfigServiceToken, DwSecurityUserService, DwSecurityTopics, DwEventService } from '@devwareapps/devware-cap';
import { defer, Observable, of, Subject, Subscription } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { AppMetaData, RecipientEntity, RecipientPhotoEntity, RecipientUpdateEntity, RecipientUpdatePhotoEntity } from '../../../meta-data/app-meta-data.service';

export type RecipientStory = Pick<RecipientEntity, 'RecipientId' | 'ChallengeDefinitionId' | 'RecipientName' | 'Slug' |
  'ShortDescription' | 'FullDescriptionHtml' | 'RevealAtDateTime' | 'DisplayOrder' | 'ChallengeDefinition'> & { Photos: RecipientPhotoEntity[] };
export type RecipientNews = Pick<RecipientUpdateEntity, 'RecipientUpdateId' | 'Title' | 'BodyHtml' | 'PublishAtDateTime'> & { Photos: RecipientUpdatePhotoEntity[] };

export function orderPhotos<T extends { IsDefault?: boolean; DisplayOrder?: number }>(photos: T[]): T[] {
  return (photos || []).slice().sort((a, b) => Number(!!b.IsDefault) - Number(!!a.IsDefault) || (a.DisplayOrder || 0) - (b.DisplayOrder || 0));
}

/** One query owner for public recipient screens. CAP owns tenant and security rules. */
@Injectable({ providedIn: 'root' })
export class RecipientRepositoryService implements OnDestroy {
  private revision = 0;
  private readonly refreshTopic = 'Fitness.Recipients.Refresh';
  private readonly changed = new Subject<void>();
  readonly changes = this.changed.asObservable();
  private readonly subscriptions = new Subscription();
  private readonly items = [AppMetaData.Recipient, AppMetaData.RecipientPhoto, AppMetaData.RecipientUpdate,
    AppMetaData.RecipientUpdatePhoto, AppMetaData.ChallengeDefinition].map(item => item.ItemDetail.itemName);
  constructor(@Inject(DwOrmDataServiceToken) private orm: DwOrmDataService, private cache: DwCacheService,
    private security: DwSecurityUserService, @Inject(DwConfigServiceToken) private config: DwConfigService, private events: DwEventService) {
    this.subscriptions.add(this.security.securityContextChanged().subscribe(() => this.invalidate()));
    this.items.forEach(item => this.subscriptions.add(events.getEvent(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(item)).subscribe(() => this.invalidate())));
  }
  refresh(): void { this.invalidate(); this.events.publishEvent(this.refreshTopic, {}); }
  private invalidate(): void { this.revision++; this.changed.next(); }
  ngOnDestroy(): void { this.subscriptions.unsubscribe(); this.changed.complete(); }

  loadRecipients(challengeId?: number, previous?: boolean): Observable<RecipientStory[]> {
    const photos = AppMetaData.RecipientPhoto.CreateQueryBuilder()
      .addFields(a => [a.RecipientPhotoId, a.RecipientId, a.MediaId, a.Caption, a.AltText, a.IsDefault, a.DisplayOrder, a.CardFitWholePhoto, a.CardPositionXPercent, a.CardPositionYPercent])
      .addOrderByDesc(a => a.IsDefault).addOrderBy(a => a.DisplayOrder).addOrderBy(a => a.RecipientPhotoId);
    const challenge = AppMetaData.ChallengeDefinition.CreateQueryBuilder()
      .addFields(a => [a.ChallengeDefinitionId, a.Name, a.ChallengeStartDate, a.IsCurrent]);
    const query = AppMetaData.Recipient.CreateQueryBuilder()
      .addFields(a => [a.RecipientId, a.ChallengeDefinitionId, a.RecipientName, a.Slug, a.ShortDescription,
        a.FullDescriptionHtml, a.RevealAtDateTime, a.DisplayOrder])
      .addFilterAnd(f => f.Equal(a => a.IsPublished, true))
      // Relative comparer is evaluated using the API clock, not the visitor's clock.
      .addFilterAnd(f => f.DateFilters.LessThanEqualHours(a => a.RevealAtDateTime, 0))
      .addOrderBy(a => a.DisplayOrder).addOrderBy(a => a.RecipientId)
      .addPrefetch(r => r.RecipientPhoto, photos.query)
      .addPrefetch(r => r.ChallengeDefinition, challenge.query);
    if (challengeId != null) { query.addFilterAnd(f => f.Equal(a => a.ChallengeDefinitionId, challengeId)); }
    if (previous != null) {
      query.addRelation(r => r.ChallengeDefinition);
      query.addFilterAnd(f => f.Equal(a => AppMetaData.ChallengeDefinition.Attributes.IsCurrent, !previous));
    }
    return this.cached('list-' + (challengeId == null ? 'all' : challengeId) + '-' + previous, () =>
      this.orm.executeQuery<RecipientEntity>(query).pipe(take(1), map(rows => rows.map(row => this.story(row)))));
  }

  loadStory(slug: string, challengeId?: number): Observable<RecipientStory> {
    if ((challengeId != null && (!Number.isSafeInteger(challengeId) || challengeId <= 0)) || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) { return of(null); }
    const query = AppMetaData.Recipient.CreateQueryBuilder()
      .addFields(a => [a.RecipientId, a.ChallengeDefinitionId, a.RecipientName, a.Slug, a.ShortDescription,
        a.FullDescriptionHtml, a.RevealAtDateTime, a.DisplayOrder])
      .addFilterAnd(f => f.Equal(a => a.Slug, slug))
      .addFilterAnd(f => f.Equal(a => a.IsPublished, true))
      .addFilterAnd(f => f.DateFilters.LessThanEqualHours(a => a.RevealAtDateTime, 0))
      .addPrefetch(r => r.RecipientPhoto, AppMetaData.RecipientPhoto.CreateQueryBuilder()
        .addFields(a => [a.RecipientPhotoId, a.RecipientId, a.MediaId, a.Caption, a.AltText, a.IsDefault, a.DisplayOrder, a.CardFitWholePhoto, a.CardPositionXPercent, a.CardPositionYPercent])
        .addOrderByDesc(a => a.IsDefault).addOrderBy(a => a.DisplayOrder).addOrderBy(a => a.RecipientPhotoId).query)
      .addPrefetch(r => r.ChallengeDefinition, AppMetaData.ChallengeDefinition.CreateQueryBuilder()
        .addFields(a => [a.ChallengeDefinitionId, a.Name, a.ChallengeStartDate, a.IsCurrent]).query);
    if (challengeId != null) { query.addFilterAnd(f => f.Equal(a => a.ChallengeDefinitionId, challengeId)); }
    return this.cached('story-' + (challengeId == null ? 'slug' : challengeId) + '-' + slug, () =>
      this.orm.executeQuery<RecipientEntity>(query).pipe(take(1), map(rows => rows.length === 1 ? this.story(rows[0]) : null)));
  }

  loadLegacyStory(challengeId: number, slug: string): Observable<RecipientStory> {
    if (!Number.isSafeInteger(challengeId) || challengeId <= 0 || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) { return of(null); }
    return this.loadStory(slug, challengeId).pipe(switchMap(story => story ? of(story) :
      // Old season-scoped links can predate a year suffix added to disambiguate a repeated name.
      this.loadRecipients(challengeId).pipe(map(rows => rows.find(row =>
        row.Slug === slug + '-' + row.ChallengeDefinition?.ChallengeStartDate?.slice(0, 4)) || null))));
  }

  loadUpdates(story: RecipientStory): Observable<RecipientNews[]> {
    if (!story) { return of([]); }
    // Recheck parent visibility before reading independently published updates.
    return this.cached('updates-' + story.RecipientId, () => this.loadStory(story.Slug, story.ChallengeDefinitionId).pipe(switchMap(current => {
      if (!current || current.RecipientId !== story.RecipientId) { return of([]); }
      const query = AppMetaData.RecipientUpdate.CreateQueryBuilder()
        .addFields(a => [a.RecipientUpdateId, a.RecipientId, a.Title, a.BodyHtml, a.PublishAtDateTime])
        .addFilterAnd(f => f.Equal(a => a.RecipientId, current.RecipientId))
        .addFilterAnd(f => f.Equal(a => a.IsPublished, true))
        .addFilterAnd(f => f.DateFilters.LessThanEqualHours(a => a.PublishAtDateTime, 0))
        .addOrderByDesc(a => a.PublishAtDateTime).addOrderByDesc(a => a.RecipientUpdateId)
        .addPrefetch(r => r.RecipientUpdatePhoto, AppMetaData.RecipientUpdatePhoto.CreateQueryBuilder()
          .addFields(a => [a.RecipientUpdatePhotoId, a.RecipientUpdateId, a.MediaId, a.Caption, a.AltText, a.IsDefault, a.DisplayOrder])
          .addOrderByDesc(a => a.IsDefault).addOrderBy(a => a.DisplayOrder).addOrderBy(a => a.RecipientUpdatePhotoId).query);
      return this.orm.executeQuery<RecipientUpdateEntity>(query).pipe(take(1), map(rows => rows.map(row => ({
        RecipientUpdateId: row.RecipientUpdateId, Title: row.Title, BodyHtml: row.BodyHtml,
        PublishAtDateTime: row.PublishAtDateTime, Photos: orderPhotos(row.RecipientUpdatePhoto),
      }))));
    })));
  }

  private cached<T>(name: string, read: () => Observable<T>): Observable<T> {
    return defer(() => {
      const scope = this.scope();
      const requireScope = (value: T) => {
        if (scope !== this.scope()) { throw new Error('The account changed while recipients were loading.'); }
        return value;
      };
      return this.cache.getWithOptions<T>('Fitness.Recipients.' + scope + '.' + name,
        defer(read).pipe(take(1), map(requireScope)), {
          storageLocation: DwCacheStorageLocation.memory, expirationInMinutes: 60,
          cacheInvalidationsTopics: [this.refreshTopic, DwSecurityTopics.SECURITY_CHANGED,
            ...this.items.map(item => DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(item))],
        }).pipe(take(1), map(requireScope));
    });
  }
  private scope(): string {
    const context = this.security.securityContext;
    return JSON.stringify([this.config.coreConfig.apiRoot, context?.ApiRoot, context?.AuthenticatedUser?.UserId,
      context?.ApplicationUser?.UserId, this.revision]);
  }

  private story(row: RecipientEntity): RecipientStory {
    // Explicit public projection also protects admin visitors from rendering private notes.
    return { RecipientId: row.RecipientId, ChallengeDefinitionId: row.ChallengeDefinitionId,
      RecipientName: row.RecipientName, Slug: row.Slug, ShortDescription: row.ShortDescription,
      FullDescriptionHtml: row.FullDescriptionHtml, RevealAtDateTime: row.RevealAtDateTime,
      DisplayOrder: row.DisplayOrder, ChallengeDefinition: row.ChallengeDefinition, Photos: orderPhotos(row.RecipientPhoto) };
  }
}
